/** @file tileStroke: what a pointer press, drag and release do on the Tile canvas with the current tool. */
import type { Image, Rgba } from '@zilo/pose3d-image-io/types/image';
import { tileBucketFill } from './tileBucketFill';
import { tileColorAt } from './tileColorAt';
import { tileDrafts } from './tileDrafts';
import { tileLinePoints } from './tileLinePoints';
import { tileMirrorX } from './tileMirrorX';
import { tilePaint } from './tilePaint';
import { tilePaintUi } from './tilePaintUi';

/** The modifier keys a press carries: Alt with the pencil picks, Shift with the pencil draws a line from the last point. */
export type TileStrokeMods = { alt: boolean; shift: boolean };

/** Kept by the canvas across strokes: the last painted pixel (Shift+click lines start there). */
export type TileStrokeMemory = { last: [number, number] | null };

/** The press, drag and release handlers in tile pixels. */
export type TileStrokeHandlers = { down: (x: number, y: number, mods: TileStrokeMods) => void; move: (x: number, y: number) => void; up: () => void };

/** One stroke in flight: free-hand or a line from `start` over the pixels `base` had at the press. */
type Live = { mode: 'none' | 'free' | 'line'; color: Rgba; base: Image | null; start: [number, number]; end: [number, number] };

const CLEAR: Rgba = [0, 0, 0, 0];

/** The pivot column while Mirror X is on, else null. */
const mirrorOf = (pivotX: number): number | null => (tilePaintUi.getState().mirror ? pivotX : null);

/** Redraws the line from `live.start` to (`x`, `y`) over the press-time pixels. */
function drawLine(key: string, pivotX: number, live: Live, x: number, y: number): void {
  live.end = [x, y];
  const { base, start, color } = live;
  tileDrafts.getState().edit(key, (img) => {
    if (base !== null) img.data.set(base.data);
    tilePaint(img, tileLinePoints(start[0], start[1], x, y), color, mirrorOf(pivotX));
    return true;
  });
}

/** One bucket fill at the seed (and its mirror under Mirror X) as one undo step. */
function bucket(key: string, pivotX: number, color: Rgba, x: number, y: number): void {
  const drafts = tileDrafts.getState();
  drafts.stroke(key);
  drafts.edit(key, (img) => {
    const m = mirrorOf(pivotX) === null ? null : tileMirrorX(x, img.width, pivotX);
    const a = tileBucketFill(img, x, y, color);
    const b = m === null ? false : tileBucketFill(img, m, y, color);
    return a || b;
  });
}

/** Starts a pencil, eraser or line stroke (one undo step); a line starts at the press or, with Shift, the last pixel. */
function begin(key: string, pivotX: number, live: Live, memory: TileStrokeMemory, at: { x: number; y: number; image: Image; line: boolean; shift: boolean }): void {
  const drafts = tileDrafts.getState();
  drafts.stroke(key);
  const from = at.line ? [at.x, at.y] as [number, number] : at.shift ? memory.last : null;
  if (from === null) {
    live.mode = 'free';
    drafts.edit(key, (img) => tilePaint(img, [[at.x, at.y]], live.color, mirrorOf(pivotX)));
    memory.last = [at.x, at.y];
    return;
  }
  Object.assign(live, { mode: 'line', base: at.image, start: from });
  drawLine(key, pivotX, live, at.x, at.y);
}

/**
 * One stroke per press = one local undo step. Pencil and eraser paint every pixel the pointer crosses (gaps joined
 * by lines); the line tool (and Shift+pencil) shows a line from its start to the pointer and keeps the last one;
 * bucket fills on press; the eyedropper (and Alt+pencil) picks the colour. Mirror X repeats each pixel and each
 * bucket seed across the pivot column (`pivotX` pixels from the left, see `tileMirrorX`).
 */
export function tileStroke(key: string, pivotX: number, memory: TileStrokeMemory): TileStrokeHandlers {
  const live: Live = { mode: 'none', color: CLEAR, base: null, start: [0, 0], end: [0, 0] };
  return {
    down: (x, y, mods) => {
      const ui = tilePaintUi.getState();
      const image = tileDrafts.getState().drafts[key]?.image;
      if (image === undefined) return;
      if (ui.tool === 'eyedropper' || (ui.tool === 'pencil' && mods.alt)) {
        const picked = tileColorAt(image, x, y);
        if (picked !== null) ui.pick(picked);
        return;
      }
      live.color = ui.tool === 'eraser' ? CLEAR : ui.color;
      if (ui.tool !== 'bucket') { begin(key, pivotX, live, memory, { x, y, image, line: ui.tool === 'line', shift: mods.shift }); return; }
      bucket(key, pivotX, live.color, x, y);
      memory.last = [x, y];
    },
    move: (x, y) => {
      if (live.mode === 'line') { drawLine(key, pivotX, live, x, y); return; }
      const last = memory.last;
      if (live.mode !== 'free' || last === null || (last[0] === x && last[1] === y)) return;
      tileDrafts.getState().edit(key, (img) => tilePaint(img, tileLinePoints(last[0], last[1], x, y), live.color, mirrorOf(pivotX)));
      memory.last = [x, y];
    },
    up: () => {
      if (live.mode === 'line') memory.last = live.end;
      live.mode = 'none';
      live.base = null;
    },
  };
}
